/* src/screens/Dashboard/utils/SidePanel/Panels/Applications/AddOrUpdateFdSuppliers.tsx */

import React, {useEffect, useMemo, useState} from 'react';
import {Route} from 'react-router-dom';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {
  apiGetAllFdSuppliers,
  apiGetFarmerSuppliersByApplicationId,
  apiCreateFdFarmerSupplier,
  apiDeleteFarmerSupplier,
} from 'constants/Api';
import {ISupplierUserProfile} from 'constants/interfaces';
import {
  FdFarmerSupplierForm,
  LinkInput,
  Submission,
  OTHER_SUPPLIER_ID,
} from 'components/applications/FdFarmerSupplierForm';

type Props = {
  user: ISupplierUserProfile;
  applicationId: number;
  initialLinks: any[];
};

const AddOrUpdateFdSuppliers: React.FC<Props> = ({
  applicationId: appIdFromProps,
  initialLinks,
}) => {
  const applicationId = Number(appIdFromProps); // 🚩 ENSURE **number**

  /* dropdown items */
  const [suppliers, setSuppliers] = useState<
    {id: string; name: string; products: {id: number; name: string}[]}[]
  >([]);

  /* DB rows for this application */
  const [rows, setRows] = useState<any[]>(initialLinks || []);

  /* UI */
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  /* ───── fetch suppliers (once) ───── */
  useEffect(() => {
    apiGetAllFdSuppliers()
      .then((list) => {
        const mapped =
          (list || []).map((s: any) => ({
            id: String(s.id),
            name: s.name,
            products: (s.products || []).map((p: any) => ({
              id: Number(p.id),
              name: p.name,
            })),
          })) ?? [];
        setSuppliers(mapped);
      })
      .catch((err) => console.error('Supplier fetch error', err));
  }, []);

  /* ───── fetch supplier-product pairs for this application ───── */
  useEffect(() => {
    apiGetFarmerSuppliersByApplicationId(applicationId)
      .then((data) => {
        if (Array.isArray(data)) {
          setRows(data.length ? data : []);
        } else {
          setRows([]);
        }
      })
      .catch((err) => {
        console.error('Pair fetch error', err);
        setRows([]);
      });
  }, [applicationId]);

  /* current key-set  (supplierId-productId) */
  const currentKeys = useMemo(
    () =>
      new Set(
        rows.map(
          (r: any) => `${r.supplierList.id}-${r.supplierProductList.id}`,
        ),
      ),
    [rows],
  );

  /* default values for the form (edit mode) */
  const defaultLinks: LinkInput[] = useMemo(() => {
    const map = new Map<string, Set<number>>();
    rows.forEach((r: any) => {
      const sid = String(r.supplierList.id);
      if (!map.has(sid)) map.set(sid, new Set<number>());
      map.get(sid)!.add(Number(r.supplierProductList.id));
    });
    const links: LinkInput[] = Array.from(map.entries()).map(
      ([supplierId, set]) => ({
        supplierId,
        productIds: Array.from(set),
      }),
    );

    // Load "Other" entries from localStorage
    try {
      const storedOtherEntries = localStorage.getItem(
        `fd-supplier-other-${applicationId}`,
      );
      if (storedOtherEntries) {
        const otherEntries: LinkInput[] = JSON.parse(storedOtherEntries);
        links.push(...otherEntries);
      }
    } catch (err) {
      console.error('Error loading Other entries from localStorage', err);
    }

    return links;
  }, [rows, applicationId]);

  /* ───── submit handler ───── */
  const submit = async (data: Submission) => {
    setLoading(true);

    const desiredKeys = new Set<string>();
    // Skip "Other" entries (they don't have productIds to create database entries)
    data.links.forEach((l) => {
      if (
        l.supplierId !== OTHER_SUPPLIER_ID &&
        l.productIds &&
        l.productIds.length > 0
      ) {
        l.productIds.forEach((pid) =>
          desiredKeys.add(`${l.supplierId}-${pid}`),
        );
      }
    });

    /* create new */
    const createOps: Promise<any>[] = [];
    desiredKeys.forEach((k) => {
      if (!currentKeys.has(k)) {
        const [supplierId, productId] = k.split('-').map(Number);
        createOps.push(
          apiCreateFdFarmerSupplier({
            applicationId,
            supplierId,
            supplierProductId: productId,
          }),
        );
      }
    });

    /* delete removed */
    const deleteOps: Promise<any>[] = [];
    currentKeys.forEach((k) => {
      if (!desiredKeys.has(k)) {
        const [supplierId, productId] = k.split('-').map(Number);
        deleteOps.push(
          apiDeleteFarmerSupplier({
            applicationId,
            supplierId,
            supplierProductId: productId,
          }),
        );
      }
    });

    await Promise.allSettled([...createOps, ...deleteOps]);

    // Save "Other" entries to localStorage
    const otherEntries = data.links.filter(
      (l) => l.supplierId === OTHER_SUPPLIER_ID && l.otherText,
    );
    try {
      if (otherEntries.length > 0) {
        localStorage.setItem(
          `fd-supplier-other-${applicationId}`,
          JSON.stringify(otherEntries),
        );
      } else {
        // Remove if no "Other" entries
        localStorage.removeItem(`fd-supplier-other-${applicationId}`);
      }
    } catch (err) {
      console.error('Error saving Other entries to localStorage', err);
    }

    setSuccess(true);
    reduxSetSidePanelEditState('saved');
    setLoading(false);
  };

  const close = () => reduxToggleSidePanel(false, null);

  /* success splash */
  if (success) {
    return (
      <Route
        render={() => (
          <div className="side-panel-success-container">
            <div className="mid-section">
              <div className="success-gif">
                <CheckIcon />
              </div>
              <div className="success-msg">Primary suppliers updated</div>
            </div>
            <div className="btn-container">
              <button
                className="primary-btn"
                onClick={() => reduxToggleSidePanel(false, null)}
              >
                Done
              </button>
            </div>
          </div>
        )}
      />
    );
  }

  /* form */
  return (
    <FdFarmerSupplierForm
      key={`${rows.length}-${defaultLinks.length}`}
      suppliers={suppliers}
      defaultLinks={defaultLinks}
      isLoading={loading}
      submitButtonTitle="Save"
      onSubmit={submit}
      onCancel={close}
    />
  );
};

export default AddOrUpdateFdSuppliers;
